'use client'
import { HOTEL_IMAGE_PLACEHOLDER } from '@/src/constants/hotels'
import { Box, Skeleton, SxProps, Theme } from '@mui/material'
import { useState } from 'react'

export interface HotelImageProps {
    src: string
    alt: string
    /** Styles applied to the <img> (sizing, objectFit, radius, etc.). */
    sx?: SxProps<Theme>
    /** Styles applied to the positioning wrapper. */
    wrapperSx?: SxProps<Theme>
    /** Hero images should load eagerly; everything else lazily (default). */
    loading?: 'lazy' | 'eager'
    /** LCP images: hint the browser to fetch ahead of other requests. */
    fetchPriority?: 'high' | 'low' | 'auto'
    onClick?: () => void
}

// Hotel/room image with a shimmer while loading and a neutral placeholder on error.
// UI-only state (load/error); safe to keep in the presentation layer.
const HotelImage: React.FC<HotelImageProps> = ({
    src,
    alt,
    sx,
    wrapperSx,
    loading = 'lazy',
    fetchPriority,
    onClick
}) => {
    const [renderedSrc, setRenderedSrc] = useState(src)
    const [loaded, setLoaded] = useState(false)
    const [failed, setFailed] = useState(false)

    // The SRP windows its card list (react-list), so a row component is reused for a
    // different hotel as you scroll. Reset load/error state when `src` changes — otherwise
    // a previous hotel's failure sticks and paints the fallback over a valid image.
    if (src !== renderedSrc) {
        setRenderedSrc(src)
        setLoaded(false)
        setFailed(false)
    }

    // A server-rendered <img> can finish loading before React hydrates, and that load event
    // is gone by the time onLoad is attached — the image would sit at opacity 0 for good.
    // Reading `complete` on mount covers exactly that case and nothing else.
    const markLoadedIfAlreadyComplete = (node: HTMLImageElement | null) => {
        if (node?.complete && node.naturalWidth > 0 && !loaded) setLoaded(true)
    }

    const resolvedSrc = failed || !src ? HOTEL_IMAGE_PLACEHOLDER : src
    const showShimmer = !loaded && !failed

    return (
        <Box
            onClick={onClick}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                bgcolor: 'neutral.100',
                cursor: onClick ? 'pointer' : 'default',
                ...wrapperSx
            }}
        >
            {showShimmer && (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                />
            )}
            <Box
                component="img"
                ref={markLoadedIfAlreadyComplete}
                src={resolvedSrc}
                alt={alt}
                loading={loading}
                fetchPriority={fetchPriority}
                decoding="async"
                draggable={false}
                onLoad={() => setLoaded(true)}
                onError={() => {
                    if (!failed) setFailed(true)
                    setLoaded(true)
                }}
                sx={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: loaded ? 1 : 0,
                    transition: 'opacity 0.25s ease',
                    ...sx
                }}
            />
        </Box>
    )
}

export { HotelImage }
export default HotelImage
